/**
 * RawJsonPanel — self-contained JSON viewer for debug inspector drawer.
 *
 * Renders pretty-printed JSON with inline syntax coloring (no external deps).
 * Includes a copy-to-clipboard button with brief feedback.
 */

import { useCallback } from 'react';
import { debugTheme } from './debugTheme';
import { useCopiedFlash } from '../../hooks/useCopiedFlash';
import { escapeHtml } from '../../components/skills/SkillDebugUtils';

const t = debugTheme.dark;

/**
 * One JSON token per match, in order: a string (plus the `:` that makes it a
 * key), a number, a keyword, a run of punctuation/whitespace, or — last — any
 * other single character, which means the input is not JSON. The catch-all
 * makes every character part of some match, so nothing is skipped unescaped.
 */
const JSON_TOKEN =
  /(?<str>"(?:[^"\\]|\\(?:["\\/bfnrt]|u[0-9a-fA-F]{4}))*")(?<colon>[ \t\n\r]*:)?|(?<num>-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?)|(?<keyword>true|false|null)|(?<punct>[{}[\],: \t\n\r]+)|(?<invalid>[\s\S])/g;

type JsonTokenGroups = Partial<Record<'str' | 'colon' | 'num' | 'keyword' | 'punct' | 'invalid', string>>;

/** Wrap one token in its color class (strings escaped; other tokens cannot hold markup). Throws on a non-JSON character. */
function renderToken(match: RegExpMatchArray): string {
  const { str, colon, num, keyword, punct, invalid }: JsonTokenGroups = match.groups ?? {};
  if (str !== undefined) {
    return colon !== undefined
      ? `<span class="text-blue-400">${escapeHtml(str)}</span>${colon}`
      : `<span class="text-green-400">${escapeHtml(str)}</span>`;
  }
  if (num !== undefined) return `<span class="text-amber-400">${num}</span>`;
  if (keyword !== undefined) return `<span class="text-purple-400">${keyword}</span>`;
  if (punct !== undefined) return punct;
  throw new Error(`colorizeJson: unexpected ${JSON.stringify(invalid)} at offset ${match.index}`);
}

/**
 * Colorize a pretty-printed JSON string as HTML. Every token is escaped, so
 * the result is safe for `dangerouslySetInnerHTML`; throws on anything that
 * is not a JSON token rather than passing it through.
 */
export function colorizeJson(json: string): string {
  return Array.from(json.matchAll(JSON_TOKEN), renderToken).join('');
}

interface RawJsonPanelProps {
  data: unknown;
}

export function RawJsonPanel({ data }: RawJsonPanelProps) {
  const [copied, flashCopied] = useCopiedFlash(2000);

  const rawJson = JSON.stringify(data, null, 2);

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(rawJson).then(() => {
      flashCopied();
    });
  }, [rawJson, flashCopied]);

  return (
    <div className="flex flex-col h-full">
      <div className={`flex items-center justify-between px-3 py-2 ${t.border} border-b`}>
        <span className={`text-[11px] font-mono ${t.textMuted} uppercase tracking-wider`}>
          Raw JSON
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className={`text-[11px] font-mono px-2 py-0.5 rounded ${t.badge} hover:bg-nightshade-600 hover:text-white transition-colors`}
        >
          {copied ? 'Copied!' : 'Copy JSON'}
        </button>
      </div>
      <div className="flex-1 overflow-auto p-3">
        <pre
          className={`text-[11px] font-mono leading-relaxed ${t.text} whitespace-pre-wrap break-words`}
          dangerouslySetInnerHTML={{ __html: colorizeJson(rawJson) }}
        />
      </div>
    </div>
  );
}
